import React, { useState } from 'react';
import moment from 'moment';

import { executeHttpRequest } from '../../epics/api';
import DocumentModal from './DocumentModal';
import { getErrorMessages, isImage } from '../../utils';
import { TFreelancerData, TDVLAConsent, TDVLAConsents } from '../../types';
import { ModalConfirmationWithErrorAndLoader } from '../common/ModalConfirmationWithErrorAndLoader';


type TFreelancerDetailsDVLAConsentsProps = {
    freelancerData: TFreelancerData;
    fetchListOf: (a: string, b: string) => Promise<any>;
    editionEnabled: boolean;
    consents: TDVLAConsents;
};

export default function FreelancerDetailsDVLAConsents({
    freelancerData,
    fetchListOf,
    editionEnabled,
    consents,
}: TFreelancerDetailsDVLAConsentsProps) {
    const [isModalAddConsentOpen, setIsModalAddConsentOpen] = useState(false);
    const [isModalConfirmationOpen, setIsModalConfirmationOpen] = useState(false);
    const [editingID, setEditingID] = useState(null);
    const [fields, setFields] = useState({ consented: null });
    const [error, setError] = useState(null);
    const [popupConsent, setPopupConsent] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleOpenAddConsentModal = () => {
        setIsModalAddConsentOpen(true);
        setError(null);
    };

    const handleCloseAddConsentModal = () => {
        setIsModalAddConsentOpen(false);
        setError(null);
    };

    const handleOpenConfirmationModal = () => {
        setIsModalConfirmationOpen(true);
        setError(null);
    };

    const handleCloseConfirmationModal = () => {
        setIsModalConfirmationOpen(false);
        setError(null);
    };

    const openPopup = (consent: TDVLAConsent) => {
        setPopupConsent(consent);
    };

    const closePopup = () => {
        setPopupConsent(null);
    };

    const handleAddConsent = () => {
        setError(null);
        setIsLoading(true);

        executeHttpRequest(
            `freelancers/${freelancerData.id}/dvla-consents/`,
            'post',
            { consented: true },
        )
            .then(() => {
                fetchListOf('consents', `freelancers/${freelancerData.id}/dvla-consents/`)
                .then(() => {
                    setIsLoading(false);
                    handleCloseAddConsentModal();
                });
            })
            .catch(err => {
                setIsLoading(false);
                setError(getErrorMessages(err));
            });
    };

    const handleEditButtonClick = (consent: TDVLAConsent) => {
        setEditingID(consent.id);
        setFields({ consented: consent.consented });
    };

    const handleCancelButtonClick = () => {
        setEditingID(null);
        setFields({ consented: null });
    };

    const handleSaveButtonClick = () => {
        setIsLoading(true);
        const consent = consents.content.find(item => item.id === editingID);

        executeHttpRequest(
            `freelancers/${freelancerData.id}/dvla-consents/${consent.id}/`,
            'patch',
            fields,
        )
            .then(() => {
                fetchListOf('consents', `freelancers/${freelancerData.id}/dvla-consents/`)
                    .then(() => {
                        setIsLoading(false);
                        handleCloseConfirmationModal();
                        handleCancelButtonClick();
                    });
            })
            .catch(err => {
                setIsLoading(false);
                setError(getErrorMessages(err));
            });
    };

    const handleCheckboxFieldChange = (event: any, fieldName: string) => {
        setFields(fields_ => ({
            ...fields_,
            [fieldName]: event.target.checked,
        }));
    };

    const renderActionButtons = (consent: TDVLAConsent) => {
        if (editionEnabled) {
            if (editingID === consent.id) {
                return (
                    <td className="actions center">
                        <button
                            onClick={handleOpenConfirmationModal}
                            type="button"
                            disabled={fields.consented === consent.consented}
                        >
                            <i className="icon icon icon-check-12" />
                        </button>
                        <button onClick={handleCancelButtonClick} type="button">
                            <i className="icon icon-close2" />
                        </button>
                    </td>
                );
            }

            return (
                <td className="actions center">
                    <button
                        onClick={() => handleEditButtonClick(consent)}
                        type="button"
                    >
                        <i className="icon icon-pencil-3" />
                    </button>
                </td>
            );
        }

        return null;
    };

    const renderLink = (consent: TDVLAConsent) => {
        if (isImage(consent.file)) {
            return <a onClick={() => openPopup(consent)}>DVLA Consent</a>;
        }
        return <a href={consent.file} target="_blank" rel="noreferrer" download={true}>DVLA Consent</a>;
    };

    const renderRows = () =>
        consents.content.map((consent: TDVLAConsent) =>
            <tr key={consent.id}>
                <td>
                    {consent.file ? renderLink(consent) : <span>DVLA Consent</span>}
                </td>
                <td>
                    {moment(consent.created).format('D MMM YYYY, HH:mm')}
                </td>
                <td>
                    {consent.expiry ? moment(consent.expiry).format('D MMM YYYY') : 'None'}
                </td>
                <td>
                    {editingID === consent.id ? (
                        <input
                            name="consented"
                            className="checkbox-purple"
                            type="checkbox"
                            checked={fields.consented}
                            onChange={event => handleCheckboxFieldChange(event, 'consented')}
                        />
                    ) : (
                        <i className={consent.consented ? 'icon icon-check-12' : 'icon icon-close2'}/>
                    )}
                </td>
                {renderActionButtons(consent)}
            </tr>

        );

    return (
        <div className="freelancer-details-section">
            <div className="freelancer-details-section__header">
                <div className="freelancer-details-section__title">
                    DVLA Consents
                </div>
                <div className="freelancer-details-section__btns display-block">
                    <button
                        className="freelancer-details-section__btn"
                        onClick={handleOpenAddConsentModal}
                        type="button"
                    >
                        <i className="icon with-text icon-add-12" />
                        Add
                    </button>
                    {!!consents.loading && (
                        <i className="icon icon-loading-1 font-size-30" />
                    )}
                </div>
            </div>
            {error && !isModalAddConsentOpen && !isModalConfirmationOpen && (
                <div className="freelancer-details-section__errors">
                    <p>{error}</p>
                </div>
            )}
            <div className="freelancer-details-section__content">
                {consents.content.length === 0 ? (
                    <div>Worker doesn&apos;t have any consents yet.</div>
                ) : (
                    <div>
                        <table className="table-default">
                            <tbody>
                                <tr>
                                    <td className="table-default__cell--text-blue">
                                        Type
                                    </td>
                                    <td className="table-default__cell--text-blue">
                                        Uploaded
                                    </td>
                                    <td className="table-default__cell--text-blue">
                                        Expiration
                                    </td>
                                    <td className="table-default__cell--text-blue">
                                        Consented
                                    </td>
                                    {editionEnabled && (
                                        <td className="table-default__cell--text-blue center" />
                                    )}
                                </tr>
                                {renderRows()}
                            </tbody>
                        </table>
                        {popupConsent && (
                            <DocumentModal
                                isOpen={true}
                                src={popupConsent.file}
                                onClose={closePopup}
                                title="DVLA Consent"
                            />
                        )}
                    </div>
                )}
                <ModalConfirmationWithErrorAndLoader
                    className={null}
                    errorMessage={error}
                    handleCancel={handleCloseAddConsentModal}
                    handleContinue={handleAddConsent}
                    isError={!!error}
                    isInProgress={isLoading}
                    setIsInProgress={setIsLoading}
                    isOpen={isModalAddConsentOpen}
                    title="Add DVLA Consent"
                    continueButtonText="CONTINUE"
                >
                    <p className="modal-title">Add DVLA Consent</p>
                    <div className="cancel-worker-body">
                        <p>
                            By pressing <b>Continue</b> below you are confirming that <b>{freelancerData.full_name}</b><br/>
                            has given you their consent for DVLA checks to take place on the driving licence.
                        </p>
                        <p className="margin-top-10">
                            A DVLA consent form will be generated, stored on the worker record<br/>
                            and emailed to the worker confirming that their consent has been given.
                        </p>
                        <p className="margin-top-10">
                            <b>Note:</b> Files are generated in the background and may take a while to appear.
                        </p>
                    </div>
                </ModalConfirmationWithErrorAndLoader>
                <ModalConfirmationWithErrorAndLoader
                    className={null}
                    errorMessage={error}
                    handleCancel={handleCloseConfirmationModal}
                    handleContinue={handleSaveButtonClick}
                    isError={!!error}
                    isInProgress={isLoading}
                    setIsInProgress={setIsLoading}
                    isOpen={isModalConfirmationOpen}
                    title="Edit DVLA Consent"
                >
                    <p className="modal-title">{fields.consented ? 'Re-instate' : 'Revoke'} DVLA Consent</p>
                    <div className="cancel-worker-body">
                        <p>Are you sure you want to {fields.consented ? 're-instate' : 'revoke'} the consent?</p>
                        {!fields.consented &&
                            <p className="margin-top-10 fo console">
                                <b>Warning:</b> If you choose to continue and revoke this worker&apos;s DVLA consent<br/>
                                you will not be able to reverse this action. The worker will need to consent<br/>
                                again via the mobile app before they can be placed into a driving assignment.
                            </p>
                        }
                    </div>
                </ModalConfirmationWithErrorAndLoader>
            </div>
        </div>
    );
}
